import i18n from "../i18n";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    AlertTriangle, AlertCircle, Bell, Syringe, Stethoscope, HardHat, Package, Boxes, RefreshCw,
    Droplet, GraduationCap, ScrollText,
} from "lucide-react";
import { toast } from "sonner";

const KIND_ICON = {
    vaccination: Syringe,
    birth: Bell,
    withdrawal: Stethoscope,
    treatment: Stethoscope,
    follow_up: Stethoscope,
    medical_schedule: Syringe,
    medical_schedule_completed: Syringe,
    gestation_due_today: AlertTriangle,
    gestation_due_soon: AlertTriangle,
    gestation_overdue: AlertTriangle,
    farrowing_followup: AlertTriangle,
    asset_overdue: HardHat,
    asset_soon: HardHat,
    low_stock: Boxes,
    feed: Boxes,
    due_task: ScrollText,
    batch_expiry: Package,
    water_quality: Droplet,
    training_expiry: GraduationCap,
    compliance: ScrollText,
};

const SEV_STYLE = {
    critical: "border-destructive/40 bg-destructive/5 text-destructive",
    warning: "border-amber-500/40 bg-amber-500/5 text-amber-800",
    info: "border-blue-500/40 bg-blue-500/5 text-blue-800",
};

export default function AlertsPage() {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        try {
            const d = await api.get("/alerts").then((r) => r.data);
            setData(d);
        } catch (err) {
            toast.error("Failed to load alerts");
            setData({ counts: { total: 0, critical: 0, warning: 0, info: 0 }, alerts: [] });
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="p-6 text-muted-foreground">{i18n.t("batch4.33ce417454bf")}</div>;
    const filtered = filter === "all" ? data.alerts : data.alerts.filter((a) => a.severity === filter);

    return (
        <div className="space-y-6" data-testid="alerts-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="tiny-label flex items-center gap-2"><Bell className="w-4 h-4" />{i18n.t("batch4.e894481d9eed")}</div>
                    <h1 className="text-3xl font-bold mt-1">{i18n.t("batch4.aee9f02c9b71")}</h1>
                    <p className="text-sm text-muted-foreground">
                        {i18n.t("batch4.5c55dc1187df")}
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={load} data-testid="alerts-refresh"><RefreshCw className="w-4 h-4 mr-1" />{i18n.t("batch4.56e3badc4e6c")}</Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SumCard label={i18n.t("batch4.b25928c69902")} value={data.counts.total} active={filter === "all"} onClick={() => setFilter("all")} testid="alerts-sum-total" />
                <SumCard label={i18n.t("batch4.04b7b26c8ca2")} value={data.counts.critical} sev="critical" active={filter === "critical"} onClick={() => setFilter("critical")} testid="alerts-sum-critical" />
                <SumCard label={i18n.t("batch4.e9c45563358e")} value={data.counts.warning} sev="warning" active={filter === "warning"} onClick={() => setFilter("warning")} testid="alerts-sum-warning" />
                <SumCard label={i18n.t("batch4.4b631f698425")} value={data.counts.info} sev="info" active={filter === "info"} onClick={() => setFilter("info")} testid="alerts-sum-info" />
            </div>

            <div className="space-y-2" data-testid="alerts-list">
                {filtered.length === 0 && (
                    <Card className="p-8 text-center text-muted-foreground text-sm">
                        {data.counts.total === 0 ? "🎉 No active alerts. Everything is on schedule." : `No ${filter} alerts.`}
                    </Card>
                )}
                {filtered.map((a) => {
                    const Icon = KIND_ICON[a.kind] || AlertCircle;
                    return (
                        <Link
                            key={a.id}
                            to={a.link || "#"}
                            className={`flex items-center gap-3 rounded-xl border p-3 hover:shadow transition ${SEV_STYLE[a.severity]}`}
                            data-testid={`alert-row-${a.id}`}
                        >
                            <div className="w-9 h-9 rounded-lg bg-white/70 grid place-items-center flex-shrink-0">
                                <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate">{a.title}</div>
                                <div className="text-xs opacity-80 truncate">{a.detail}</div>
                            </div>
                            <Badge variant="outline" className="capitalize flex-shrink-0">{a.severity}</Badge>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

function SumCard({ label, value, sev, active, onClick, testid }) {
    const bg = sev === "critical" ? "bg-destructive/10 border-destructive/40"
        : sev === "warning" ? "bg-amber-500/10 border-amber-500/40"
        : sev === "info" ? "bg-blue-500/10 border-blue-500/40"
        : "bg-secondary border-border";
    return (
        <button
            className={`text-left rounded-xl border p-3 transition ${bg} ${active ? "ring-2 ring-primary" : ""}`}
            onClick={onClick}
            data-testid={testid}
        >
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
                <AlertTriangle className="w-3.5 h-3.5" />{label}
            </div>
            <div className="text-2xl font-bold mt-1">{value}</div>
        </button>
    );
}
